import { handleApiCall } from "../api";

export const getStore = (page: number = 1, limit: number = 10) => handleApiCall("get", `stores?page=${page}&limit=${limit}`);

export const getByStoreID = (storeId: number) => handleApiCall("get", `stores/${storeId}`);

// POST /stores is intentionally slow today: it does an S3 logo upload,
// bcrypt hash, role lookup, and several DB writes inside a single
// transaction. The global ApiClient timeout (60s) is too tight for this
// endpoint on slower networks. We override it to 3 minutes *here only* so
// other endpoints keep their fast-fail behaviour, and handleApiCall's UI
// watchdog auto-aligns with this value.
//
// NOTE: this is a ceiling — fast responses are unaffected. Once POST /stores
// p95 is under ~2s (profiling + idempotency-key work), drop this back.
export const createStore = (postPayload: any) =>
  handleApiCall(
    "post",
    `stores/`,
    postPayload,
    {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    }
  );

export const updateStore = (storeId: number, patchPayload:any ) => handleApiCall("patch", `stores/${storeId}`, patchPayload, { headers: { "Content-Type": "multipart/form-data" } });

export const deleteSelectedStoreImage = (storeId: number, imageLabel: string) => handleApiCall("delete", `stores/${storeId}/delete-image/${imageLabel}`);

// --- Soft-delete (trash) / restore -------------------------------------------
// Stores are never hard-deleted. Trashing hides them from the seller list and
// the buyer app; they stay restorable for 24h, after which they drop out of the
// trash view automatically.

/** Move a single store to trash (soft-delete). */
export const trashStore = (storeId: number) => handleApiCall("delete", `stores/${storeId}`);

/** Move several stores to trash in one request. */
export const bulkTrashStores = (ids: number[]) => handleApiCall("post", `stores/bulk-delete`, { ids });

/** Restore a trashed store back to the list (only within the 24h window). */
export const restoreStore = (storeId: number) => handleApiCall("patch", `stores/${storeId}/restore`);

/** Restore several trashed stores in one request. */
export const bulkRestoreStores = (ids: number[]) => handleApiCall("post", `stores/bulk-restore`, { ids });

/** List stores currently in trash (within 24h) + count for the badge. */
export const getTrashedStores = () => handleApiCall("get", `stores/trashed`);

// Store state management APIs
export const getStoreState = (storeId: number) => handleApiCall("get", `stores/${storeId}/state`);

export const updateStoreOverride = (storeId: number, payload: { state: string; ttl_seconds?: number; reason_code?: string; note?: string }) => handleApiCall("put", `stores/${storeId}/override`, payload);

/**
 * Send this store's menu to the buyer app.
 *
 * Replaces /ondc/sync-store-to-buyer, which only asked the buyer to run an
 * ONDC SEARCH against an external adapter. That adapter rejects every search
 * with "30016 Invalid Signature", so the buyer catalogue never actually
 * refreshed and a newly added dish stayed invisible to customers. This
 * endpoint carries the menu itself, over the same queue that delivers orders.
 */
export const syncStoreToBuyer = (storeId: number) => handleApiCall("post", `/ondc/push-menu-to-buyer`, { store_id: storeId });

export const getStoreStatusOverrideLogs = (
  page: number = 1,
  limit: number = 20,
  storeId?: number,
) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (storeId) {
    params.append("store_id", String(storeId));
  }
  return handleApiCall("get", `stores/status-override-logs?${params.toString()}`);
};